/**
 * HustleMeetupsOverviewPage.jsx — served at `hustlemeetups.com/`
 * (bare-domain root). Public, no login required. High-level pitch of
 * the HustleMeetups module with three tiles linking to the individual
 * sales pages (Meet · Circles · Matchmaker).
 *
 * Uses the shared PlatformHeader (which auto-shows the module tabs +
 * hides Emergency for hustlemeetups.com via agency settings).
 *
 * Light + Dark themes fully supported. Icons carry `title` attributes
 * for hover tooltips + a11y.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, ChefHat, Sparkles, Shield, Heart, CheckCircle2, ArrowRight } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { hm } from "@/lib/hmCopy";

/* ── Long-form sales article rendered on the /hustlemeetups landing.
 *  Content sourced verbatim from user's "New Sales Copy" Word doc
 *  (28 Feb 2026). Kept in-component (rather than i18n keys) because
 *  the doc is English-only for now; a batch translation pass will
 *  hoist these into hmCopy.js once the Universal LLM key is topped up. */
const SALES_INTRO_PARAS = [
  "There are more ways to communicate than ever before. We message. We post. We follow. We comment. We join online communities and sit through video calls. Yet sometimes the simplest thing is becoming the hardest: Actually meeting people.",
  "HustleMeetups was created to make that easier. It's a place where verified members can discover groups, join communities that interest them and attend meetups happening in their local area or online.",
  "Some meetups are professional. Some are purely social. Some revolve around an activity, hobby or shared interest. And some are simply an excuse for a group of people to get together for dinner, coffee, music or a good conversation. There doesn't need to be anything complicated about it. Find something you like. Join the group. See what's happening. Come along.",
];

const SALES_SECTIONS = [
  {
    title: "One Membership. Hundreds of Reasons to Get Out and Meet People.",
    paragraphs: [
      "Your interests don't fit neatly into one category. Neither should your meetup community. You might be a business owner who wants to meet other local professionals. You might also enjoy live music. Perhaps you like trying new restaurants. Maybe you'd enjoy a Sunday morning walking group, an occasional dinner or meeting other people interested in technology, property, photography, travel, food, entrepreneurship or your local community.",
      "Why should you need completely different platforms for each part of your life? HustleMeetups brings them together.",
      "Browse groups based on your interests, profession, location or activities and join the ones that appeal to you. When a group announces its next meetup, decide whether you'd like to attend. Simple.",
    ],
  },
  {
    title: "Professional Meetups Without Awkward Networking",
    paragraphs: [
      "Professional networking doesn't have to mean walking into a room full of strangers, collecting business cards and delivering your elevator pitch twenty times. Sometimes the best professional relationships begin with a normal conversation.",
      "HustleMeetups allows professional communities to organise meetings in places appropriate for the group. That could mean: a conference or meeting room; a hotel function space; breakfast at a local café; lunch at a restaurant; an industry presentation; a workshop; an informal business gathering; or an online video conference.",
      "Groups might bring together entrepreneurs, local business owners, professionals, tradespeople, marketers, technology specialists, property professionals, investors, creatives or people working within the same industry. You decide which communities are relevant to you. Then you decide which events you want to attend.",
      "There is no requirement to turn up every week simply because you joined a group. Come when the topic, people, location or event interests you.",
    ],
  },
  {
    title: "And Sometimes You Just Want to Meet People",
    paragraphs: [
      "Not every meetup needs a business purpose. Sometimes you simply want to get out of the house and do something enjoyable with other people. HustleMeetups can include groups meeting at: restaurants, cafés, piano bars, live music venues, parks, markets, galleries, local events, walking trails, community venues, and countless other places.",
      "Imagine discovering that a local group is meeting for dinner at a restaurant you've wanted to try. Join them. A jazz or piano group is heading somewhere Friday night? Put your name down. A casual Sunday morning walking group is meeting at the park? Come along. A local food group is visiting a new restaurant next month? Reserve your place.",
      "The activity gives everyone a reason to be there — and conversation has somewhere natural to begin.",
    ],
  },
  {
    title: "Join the Group. Not an Obligation.",
    paragraphs: [
      "Joining a HustleMeetups group doesn't mean committing your calendar to every event it runs. Think of a group as a community you're interested in. You might join five groups and only attend one meetup this month. Next month, another three events might catch your attention. That's completely fine.",
      "Your groups help you discover relevant events without requiring you to constantly search for things to do. When something sounds good, RSVP and come along. When it doesn't, wait for the next one. Your groups. Your interests. Your choice.",
    ],
  },
  {
    title: "Verified Members Make a Difference",
    paragraphs: [
      "One of the most important differences with HustleMeetups is something you can't always see from an event listing: Our members are verified.",
      "Online communities have made it incredibly easy to create accounts, but that has also created fake profiles, bots, spam accounts and people pretending to be somebody they're not. HustleMeetups is being built around a different principle. People should have greater confidence that the people they're meeting are real people.",
      "Verification doesn't mean everyone will become your best friend. It doesn't replace normal personal safety precautions. But it does create an important additional layer of trust and accountability within the community.",
      "Whether you're attending a professional breakfast, joining a social dinner or meeting a group at a local event, knowing that membership is verified helps create a better foundation for genuine communities.",
    ],
  },
  {
    title: "Create Your Own Group",
    paragraphs: [
      "Sometimes the group you're looking for doesn't exist yet. So create it.",
      "Perhaps you'd like to start: a monthly local business breakfast, a technology founders group, a Friday evening dinner club, a live music group, a walking group, a local photography community, a professional discussion group, a monthly restaurant club, or simply a social group for people in your area.",
      "You choose what the group is about. You choose the style of meetup. You choose when it meets. And importantly, you aren't locked into one venue. A dinner group could visit a different restaurant each month. A business group might use a conference room for presentations and a restaurant for informal networking. A social group could meet at a café this week, a park next month and a music venue after that. The group is the community. The venue is simply where you're meeting next.",
    ],
  },
  {
    title: "Meet Online Too",
    paragraphs: [
      "Real connections don't always have to begin in the same room. Professional groups, educational communities and special-interest groups can also hold online meetups and video conferences. A group could therefore combine both. Meet online for regular discussions. Get together physically once a month. Run an online presentation followed by an in-person networking event. HustleMeetups isn't about forcing one particular way of connecting. It's about making it easier for people with something in common to find one another and meet.",
    ],
  },
  {
    title: "Your Existing Friends Are Welcome Too",
    paragraphs: [
      "HustleMeetups isn't only about meeting strangers. You can also use groups to organise people you already know. Create a private dinner group. Bring together former colleagues. Create a regular catch-up for an existing group of friends. Organise your professional circle.",
      "Instead of dozens of messages asking: \u201CWho's free?\u201D \u201CWhat date?\u201D \u201CWhere should we go?\u201D \u201CWho's actually coming?\u201D Create the meetup and let everyone RSVP. Your community can include old friends, new friends, professional contacts — or a mixture of all three.",
    ],
  },
  {
    title: "Different People. Different Interests. One Community.",
    paragraphs: [
      "The point of HustleMeetups isn't to tell you how to socialise. It's to give you more opportunities to do it. Some people want to expand their professional network. Some want to make new friends. Some have moved to a new suburb or city. Some want people to enjoy their hobbies with. Some simply realise they've been spending too much time behind a screen and would like to get out more.",
      "All are good reasons to join. Because genuine communities aren't built from follower counts. They're built from conversations. Shared experiences. Familiar faces. The person you met at breakfast who becomes a business contact. The couple you met at a restaurant who become friends. The group you joined casually and are still meeting years later. Those relationships have to start somewhere.",
    ],
  },
  {
    title: "Find Your People",
    paragraphs: [
      "You don't need another hundred online connections. Sometimes you need five people around a restaurant table. Twenty people sharing ideas in a conference room. A group walking through a park on Sunday morning. Friends listening to live music on Friday night. Or professionals discussing an idea over coffee.",
      "That's what HustleMeetups is designed to make possible. Join groups that interest you. Discover meetups happening around you. Choose the ones you want to attend. And meet people knowing you're part of a community built around verified membership.",
    ],
  },
];

/* ── iter329: Ambient hero + tile photos.
 * User feedback: overview page needed real photos, not just icons.
 * These are direct-from-Unsplash CDN URLs (people meeting at
 * restaurants, cafes and social settings) resized to 800px for weight. */
const OVERVIEW_IMG = {
  hero:       "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?auto=format&fit=crop&w=1400&q=70",
  matchmaker: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=70",
  circles:    "https://images.unsplash.com/photo-1529543544282-ea669407fca3?auto=format&fit=crop&w=800&q=70",
  meet:       "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=800&q=70",
  trust:      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=70",
};

function Tile({ testid, to, kicker, title, body, cta, Icon, iconTitle, tint, photo }) {
  const gradClass = {
    amber:  "from-amber-500/25 to-orange-600/25 dark:from-amber-500/20 dark:to-orange-600/20",
    orange: "from-orange-500/25 to-rose-600/25 dark:from-orange-500/20 dark:to-rose-600/20",
    cyan:   "from-cyan-500/25 to-sky-600/25 dark:from-cyan-500/20 dark:to-sky-600/20",
  }[tint];
  const borderClass = {
    amber:  "border-amber-500/60 hover:border-amber-500 dark:border-amber-500/40 dark:hover:border-amber-400",
    orange: "border-orange-500/60 hover:border-orange-500 dark:border-orange-500/40 dark:hover:border-orange-400",
    cyan:   "border-cyan-500/60 hover:border-cyan-500 dark:border-cyan-500/40 dark:hover:border-cyan-400",
  }[tint];
  const iconClass = {
    amber: "text-amber-700 dark:text-amber-300",
    orange: "text-orange-700 dark:text-orange-300",
    cyan: "text-cyan-700 dark:text-cyan-300",
  }[tint];
  const kickerClass = iconClass;
  const ctaClass = {
    amber:  "bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-stone-950",
    orange: "bg-orange-600 hover:bg-orange-700 text-white dark:bg-orange-500 dark:hover:bg-orange-400 dark:text-stone-950",
    cyan:   "bg-cyan-700 hover:bg-cyan-800 text-white dark:bg-cyan-500 dark:hover:bg-cyan-400 dark:text-stone-950",
  }[tint];

  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative rounded-3xl border ${borderClass}
        bg-gradient-to-br ${gradClass}
        bg-white/70 dark:bg-stone-900/40
        backdrop-blur-sm p-8 sm:p-10 transition-all
        hover:-translate-y-1 flex flex-col shadow-sm hover:shadow-md overflow-hidden`}
    >
      {photo && (
        <img
          src={photo}
          alt={iconTitle}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-15 dark:opacity-10 pointer-events-none"
        />
      )}
      <div className="relative">
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl
        bg-white dark:bg-stone-900/70 border border-stone-200 dark:border-stone-700 ${iconClass}`}
        title={iconTitle}>
        <Icon size={22} strokeWidth={1.75} aria-label={iconTitle} />
      </div>
      <p className={`mt-6 text-[11px] font-black uppercase tracking-[0.3em] ${kickerClass}`}>{kicker}</p>
      <h3 className="mt-2 text-2xl sm:text-3xl font-black leading-tight text-stone-900 dark:text-stone-50">{title}</h3>
      <p className="mt-3 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed flex-1">{body}</p>
      <div className="mt-6">
        <span className={`inline-flex items-center gap-2 rounded-full ${ctaClass}
          font-bold text-sm px-5 py-2.5 transition-transform group-hover:translate-x-0.5`}>
          {cta} <ArrowRight size={15} aria-hidden="true" />
        </span>
      </div>
      </div>
    </Link>
  );
}

function TrustCard({ Icon, iconTitle, title, body }) {
  return (
    <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6 shadow-sm">
      <span className="inline-block" title={iconTitle}>
        <Icon
          size={20}
          className="text-cyan-700 dark:text-cyan-300"
          strokeWidth={1.75}
          aria-label={iconTitle}
        />
      </span>
      <h4 className="mt-4 font-black text-stone-900 dark:text-stone-50">{title}</h4>
      <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">{body}</p>
    </div>
  );
}

export default function HustleMeetupsOverviewPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = hm(t, "overview.page_title");
  }, [t]);

  const heroTitle = hm(t, "overview.hero_title");
  const heroAccent = hm(t, "overview.hero_title_accent");
  const [before, after] = heroTitle.includes(heroAccent)
    ? heroTitle.split(heroAccent)
    : [heroTitle, ""];

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 dark:bg-stone-950 dark:text-stone-50" data-testid="hustlemeetups-overview-page">
      <PlatformHeader
        brandName="HustleMeetups"
        brandColor="#06b6d4"
        brandInitials="HM"
        showEmergency={false}
      />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-orange-600/10 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-cyan-700 dark:text-cyan-300">
            {hm(t, "overview.kicker")}
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-3xl text-stone-900 dark:text-stone-50"
              data-testid="overview-hero-title">
            {before}
            <span className="text-cyan-700 dark:text-cyan-300">{heroAccent}</span>
            {after}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone-700 dark:text-stone-200 max-w-2xl leading-relaxed">
            {hm(t, "overview.hero_sub")}
          </p>
          <div
            className="mt-5 max-w-3xl space-y-4 text-sm sm:text-base text-stone-600 dark:text-stone-300 leading-relaxed"
            data-testid="overview-hero-short-desc"
          >
            {(() => {
              // Split the source string on paragraph breaks and render
              // each block as its own <p>. Bold the verified-community
              // sentence that the user flagged in the Word doc.
              const desc = hm(t, "overview.hero_short_desc") || "";
              const emphasis = "Every HustleMeetups member is verified, helping create a safer, more trusted community.";
              const paras = desc.split(/\n{2,}/);
              return paras.map((para, i) => {
                const idx = para.indexOf(emphasis);
                if (idx === -1) {
                  return <p key={i}>{para}</p>;
                }
                return (
                  <p key={i}>
                    {para.slice(0, idx)}
                    <strong
                      className="font-bold text-stone-900 dark:text-stone-100"
                      data-testid="overview-hero-short-desc-emphasis"
                    >
                      {emphasis}
                    </strong>
                    {para.slice(idx + emphasis.length)}
                  </p>
                );
              });
            })()}
          </div>

          {/* ── Long-form sales article (verbatim from user's Word doc). */}
          <article
            data-testid="overview-sales-article"
            className="mt-14 max-w-3xl border-t border-stone-200 dark:border-stone-800 pt-10"
          >
            <p
              className="text-2xl sm:text-3xl font-black leading-snug text-stone-900 dark:text-stone-50"
              data-testid="overview-sales-lead"
            >
              Real People. Real Groups.{" "}
              <span className="text-cyan-700 dark:text-cyan-300">Real Connections.</span>
            </p>
            {SALES_INTRO_PARAS.map((para, i) => (
              <p
                key={`intro-${i}`}
                className="mt-4 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed"
              >
                {para}
              </p>
            ))}
            {SALES_SECTIONS.map((s, i) => (
              <section
                key={`sec-${i}`}
                data-testid={`overview-sales-section-${i}`}
                className="mt-10"
              >
                <h2 className="text-2xl sm:text-3xl font-black leading-tight text-stone-900 dark:text-stone-50">
                  {s.title}
                </h2>
                {s.paragraphs.map((para, pi) => (
                  <p
                    key={`p-${pi}`}
                    className="mt-4 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed"
                  >
                    {para}
                  </p>
                ))}
              </section>
            ))}
            <div
              data-testid="overview-sales-signature"
              className="mt-14 pt-8 border-t border-stone-200 dark:border-stone-800"
            >
              <p className="text-lg sm:text-xl font-black text-stone-900 dark:text-stone-50">
                HustleMeetups
              </p>
              <p className="mt-1 text-lg sm:text-xl font-bold text-stone-800 dark:text-stone-100">
                Verified People. Real Groups.{" "}
                <span className="text-cyan-700 dark:text-cyan-300">Real Places.</span>
              </p>
            </div>
          </article>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="https://app.hustlemeetups.au/signup"
              data-testid="overview-cta-join"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black px-6 h-12 transition-colors"
              title="Join Hustle Membership — free"
            >
              {hm(t, "overview.cta_join") || "Join Hustle Membership"} <ArrowRight size={16} aria-hidden="true" />
            </a>
            <a
              href="https://app.hustlemeetups.au/login"
              data-testid="overview-cta-login"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full border-2 border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100 hover:bg-stone-900 hover:text-white dark:hover:bg-stone-100 dark:hover:text-stone-900 font-black px-6 h-12 transition-colors"
              title="Sign in with your HustleSuite account"
            >
              {hm(t, "overview.cta_login") || "Sign in"}
            </a>
          </div>
        </div>
      </section>

      {/* ── Three Modes ─────────────────────────────────────────── */}
      <section
        data-testid="overview-three-modes"
        className="border-t border-stone-200 dark:border-stone-800 bg-white/60 dark:bg-stone-950/60"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-cyan-700 dark:text-cyan-300">
            Three Modes. One Membership.
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-3xl text-stone-900 dark:text-stone-50">
            Not a level system. <span className="text-cyan-700 dark:text-cyan-300">Different mode for different mood.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed max-w-3xl">
            Some nights you want a curated table with people you've never met. Some nights you want the regular crew at the standing venue. Some nights you just want out of the house and a group discount at a live event. HustleMeetups covers all three modes on one membership, one profile, one verified identity. Move between them freely.
          </p>

          {/* Mode cards */}
          <div className="mt-12 grid lg:grid-cols-3 gap-6" data-testid="overview-modes-grid">
            {/* MATCHMAKER */}
            <div
              data-testid="mode-card-matchmaker"
              className="rounded-3xl border-2 border-cyan-500/40 hover:border-cyan-500 bg-gradient-to-br from-cyan-500/10 to-sky-600/10 dark:from-cyan-500/15 dark:to-sky-600/15 p-7 sm:p-8 shadow-sm hover:shadow-lg transition-all flex flex-col"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white dark:bg-stone-900/70 border border-cyan-500/40 text-cyan-700 dark:text-cyan-300"
                   title="Matchmaker — curated seating">
                <Sparkles size={22} strokeWidth={1.75} aria-label="Matchmaker" />
              </div>
              <p className="mt-6 text-[11px] font-black uppercase tracking-[0.3em] text-cyan-700 dark:text-cyan-300">
                Discreet. Confidential.
              </p>
              <h3 className="mt-1 text-2xl font-black text-stone-900 dark:text-stone-50">
                Matchmaker
              </h3>
              <p className="mt-3 text-sm text-stone-700 dark:text-stone-200 leading-relaxed">
                For people who want curated, longer conversations with the right strangers. Take a ten-minute personality quiz once. Every fortnight our algorithm seats you with five complementary strangers at a partnered café, restaurant, wine bar or cocktail lounge. Anonymous first-name-only until you sit down. Never the same table twice.
              </p>
              <div className="mt-5 space-y-2 text-sm text-stone-800 dark:text-stone-100">
                <div className="flex gap-3"><span className="text-cyan-700 dark:text-cyan-300 font-black">FORMAT</span><span>Curated 6-seat table · 3 hours · partnered venue</span></div>
                <div className="flex gap-3"><span className="text-cyan-700 dark:text-cyan-300 font-black">MODEL</span><span>Big-5 personality &times; life stage &times; location</span></div>
                <div className="flex gap-3"><span className="text-cyan-700 dark:text-cyan-300 font-black">PRIVACY</span><span>First name only · no LinkedIn · confidential</span></div>
                <div className="flex gap-3"><span className="text-cyan-700 dark:text-cyan-300 font-black">CADENCE</span><span>Fortnightly · you pick which invitations to accept</span></div>
              </div>
              <div className="mt-6">
                <Link
                  to="/matchmaker"
                  data-testid="mode-card-matchmaker-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-cyan-700 hover:bg-cyan-800 text-white dark:bg-cyan-500 dark:hover:bg-cyan-400 dark:text-stone-950 font-black text-sm px-5 py-2.5 transition-colors"
                >
                  How Matchmaker works <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* CIRCLES */}
            <div
              data-testid="mode-card-circles"
              className="rounded-3xl border-2 border-orange-500/40 hover:border-orange-500 bg-gradient-to-br from-orange-500/10 to-rose-600/10 dark:from-orange-500/15 dark:to-rose-600/15 p-7 sm:p-8 shadow-sm hover:shadow-lg transition-all flex flex-col"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white dark:bg-stone-900/70 border border-orange-500/40 text-orange-700 dark:text-orange-300"
                   title="Circles — partnered recurring groups">
                <ChefHat size={22} strokeWidth={1.75} aria-label="Circles" />
              </div>
              <p className="mt-6 text-[11px] font-black uppercase tracking-[0.3em] text-orange-700 dark:text-orange-300">
                Recurring. Partnered. Real venues.
              </p>
              <h3 className="mt-1 text-2xl font-black text-stone-900 dark:text-stone-50">
                Circles
              </h3>
              <p className="mt-3 text-sm text-stone-700 dark:text-stone-200 leading-relaxed">
                For friends, colleagues and acquaintances who want to socialise regularly at real venues. Every Circle is anchored to a partner restaurant, café, coffee shop, wine bar, cocktail lounge, pub, cruise line or travel agency. Discover any Circle. Apply to join. Once accepted, the venue, cadence and chat unlock.
              </p>
              <div className="mt-5 space-y-2 text-sm text-stone-800 dark:text-stone-100">
                <div className="flex gap-3"><span className="text-orange-700 dark:text-orange-300 font-black">FORMAT</span><span>Recurring group · rotating partner venues · 4-30 members</span></div>
                <div className="flex gap-3"><span className="text-orange-700 dark:text-orange-300 font-black">MODEL</span><span>Browse open · apply to join · gated details unlock</span></div>
                <div className="flex gap-3"><span className="text-orange-700 dark:text-orange-300 font-black">PARTNERS</span><span>Restaurants · cafés · clubs · cruises · travel agencies</span></div>
                <div className="flex gap-3"><span className="text-orange-700 dark:text-orange-300 font-black">EXTRA</span><span>Attach a Circle to any Meet group — one brand, two rungs</span></div>
              </div>
              <div className="mt-6">
                <Link
                  to="/circles"
                  data-testid="mode-card-circles-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-orange-600 hover:bg-orange-700 text-white dark:bg-orange-500 dark:hover:bg-orange-400 dark:text-stone-950 font-black text-sm px-5 py-2.5 transition-colors"
                >
                  Browse Circles <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* MEET */}
            <div
              data-testid="mode-card-meet"
              className="rounded-3xl border-2 border-amber-500/40 hover:border-amber-500 bg-gradient-to-br from-amber-500/10 to-orange-600/10 dark:from-amber-500/15 dark:to-orange-600/15 p-7 sm:p-8 shadow-sm hover:shadow-lg transition-all flex flex-col"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white dark:bg-stone-900/70 border border-amber-500/40 text-amber-700 dark:text-amber-300"
                   title="Meet — interest groups + group discounts">
                <Users size={22} strokeWidth={1.75} aria-label="Meet" />
              </div>
              <p className="mt-6 text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 dark:text-amber-300">
                Open. Friendly. Group-priced.
              </p>
              <h3 className="mt-1 text-2xl font-black text-stone-900 dark:text-stone-50">
                Meet
              </h3>
              <p className="mt-3 text-sm text-stone-700 dark:text-stone-200 leading-relaxed">
                For the times you just want to get out and enjoy the company of others in a friendly, safe environment — often with cheaper group-ticket admission at partnered events. Browse any group. Join instantly. RSVP to events that interest you. Come along.
              </p>
              <div className="mt-5 space-y-2 text-sm text-stone-800 dark:text-stone-100">
                <div className="flex gap-3"><span className="text-amber-700 dark:text-amber-300 font-black">FORMAT</span><span>Open interest groups · public events · any size</span></div>
                <div className="flex gap-3"><span className="text-amber-700 dark:text-amber-300 font-black">MODEL</span><span>Join instantly · RSVP to what you like · zero obligation</span></div>
                <div className="flex gap-3"><span className="text-amber-700 dark:text-amber-300 font-black">VALUE</span><span>Group admission discounts at partnered events</span></div>
                <div className="flex gap-3"><span className="text-amber-700 dark:text-amber-300 font-black">PATH UP</span><span>Regulars can graduate into an attached Circle</span></div>
              </div>
              <div className="mt-6">
                <Link
                  to="/meet"
                  data-testid="mode-card-meet-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-stone-950 font-black text-sm px-5 py-2.5 transition-colors"
                >
                  Explore Meet <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>

          {/* Path panel — Meet → Circles graduation explainer */}
          <div
            data-testid="overview-attached-panel"
            className="mt-12 rounded-3xl border-2 border-dashed border-stone-300 dark:border-stone-700 bg-white/70 dark:bg-stone-900/40 p-7 sm:p-8"
          >
            <p className="text-[11px] font-black uppercase tracking-[0.3em] text-stone-600 dark:text-stone-400">
              New · Attached Circles
            </p>
            <h3 className="mt-2 text-2xl sm:text-3xl font-black text-stone-900 dark:text-stone-50">
              One group name. <span className="text-orange-600 dark:text-orange-400">Two attached groups.</span>
            </h3>
            <p className="mt-3 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed max-w-3xl">
              Group owners can now attach a Circle to any Meet group they run. The Meet group stays open and welcoming for everyone. The attached Circle stays small, curated and gated for the loyal core who show up regularly. Members graduate into the Circle automatically once they've RSVP'd to a configurable number of Meet events, so attendance turns into a genuine reward instead of a chase. One brand, one dashboard, two rungs — easier for owners to run, easier for members to pick which event fits their mood.
            </p>
            <ul className="mt-5 grid sm:grid-cols-3 gap-3 text-sm text-stone-800 dark:text-stone-100">
              <li className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 p-4">
                <span className="block font-black text-orange-700 dark:text-orange-300 text-[11px] uppercase tracking-widest">For members</span>
                Turn up to Meet events. Reliability score grows. Unlock a warmer, smaller Circle in the same brand.
              </li>
              <li className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 p-4">
                <span className="block font-black text-orange-700 dark:text-orange-300 text-[11px] uppercase tracking-widest">For owners</span>
                Keep your Meet group open for growth. Keep your Circle small for depth. One brand, two rungs.
              </li>
              <li className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 p-4">
                <span className="block font-black text-orange-700 dark:text-orange-300 text-[11px] uppercase tracking-widest">For venues</span>
                Standing bookings from the Circle. Bigger open events from the Meet group. Both under one partnership.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── Three tiles ──────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid md:grid-cols-3 gap-5" data-testid="overview-tiles-grid">
          <Tile
            testid="tile-meet"
            to="/meet"
            kicker={hm(t, "overview.tile_meet_kicker")}
            title={hm(t, "overview.tile_meet_title")}
            body={hm(t, "overview.tile_meet_body")}
            cta={hm(t, "overview.tile_meet_cta")}
            Icon={Users}
            iconTitle="HustleMeet — Interest groups"
            tint="amber"
            photo={OVERVIEW_IMG.meet}
          />
          <Tile
            testid="tile-circles"
            to="/circles"
            kicker={hm(t, "overview.tile_circles_kicker")}
            title={hm(t, "overview.tile_circles_title")}
            body={hm(t, "overview.tile_circles_body")}
            cta={hm(t, "overview.tile_circles_cta")}
            Icon={ChefHat}
            iconTitle="Circles — Your crew, new venue"
            tint="orange"
            photo={OVERVIEW_IMG.circles}
          />
          <Tile
            testid="tile-matchmaker"
            to="/matchmaker"
            kicker={hm(t, "overview.tile_matchmaker_kicker")}
            title={hm(t, "overview.tile_matchmaker_title")}
            body={hm(t, "overview.tile_matchmaker_body")}
            cta={hm(t, "overview.tile_matchmaker_cta")}
            Icon={Sparkles}
            iconTitle="Matchmaker — AI-picked table"
            tint="cyan"
            photo={OVERVIEW_IMG.matchmaker}
          />
        </div>
      </section>

      {/* ── Trust strip ──────────────────────────────────────────── */}
      <section className="border-t border-stone-200 dark:border-stone-800 bg-stone-100/70 dark:bg-stone-900/30">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-cyan-700 dark:text-cyan-300">
            {hm(t, "overview.trust_kicker")}
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black leading-tight max-w-xl text-stone-900 dark:text-stone-50"
              data-testid="overview-trust-title">
            {hm(t, "overview.trust_title")}
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
               data-testid="overview-trust-grid">
            <TrustCard Icon={CheckCircle2} iconTitle="Verified members" title={hm(t, "overview.trust_1_title")} body={hm(t, "overview.trust_1_body")} />
            <TrustCard Icon={ChefHat}      iconTitle="Real hosts"      title={hm(t, "overview.trust_2_title")} body={hm(t, "overview.trust_2_body")} />
            <TrustCard Icon={Heart}        iconTitle="Friendship first" title={hm(t, "overview.trust_3_title")} body={hm(t, "overview.trust_3_body")} />
            <TrustCard Icon={Shield}       iconTitle="Zero-tolerance moderation" title={hm(t, "overview.trust_4_title")} body={hm(t, "overview.trust_4_body")} />
          </div>
        </div>
      </section>

      <SuiteFooter />
    </div>
  );
}
